import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Eager load critical components
import CompanyDashboardSidebar from './components/CompanyDashboardSidebar';
import UnifiedHeader from '../../components/unified/UnifiedHeader';
import { LazyLoadBoundary, ComponentLoader, AnalyticsChartSkeleton, TableRowSkeleton } from '../../components/LazyLoadBoundary';
import { DashboardCardSkeleton } from '../../components/LazyLoadBoundary';
import CandidatesManager from './components/CandidatesManager';
import AnalyticsReports from './components/AnalyticsReports';

// Lazy load heavy dashboard components with smart chunking
const DashboardOverview = lazy(() => import('./components/DashboardOverview'));
const JobsManager = lazy(() => import('./components/JobsManager'));
const TeamManagement = lazy(() => import('./components/TeamManagement'));
const CompanyProfile = lazy(() => import('./components/CompanyProfile'));
const BillingSubscription = lazy(() => import('./components/BillingSubscription'));
const LocationsManagement = lazy(() => import('./components/LocationsManagement'));

const CompanyDashboardHub = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userRole, setUserRole] = useState('admin'); // 'admin' or 'recruiter'
  const location = useLocation();
  const { companyId } = useParams();

  // Mock data for header
  const mockNotifications = [
    { id: 1, title: 'New application', message: 'Jane Smith applied for Senior Developer position', time: '5 min ago', unread: true },
    { id: 2, title: 'Interview scheduled', message: 'Technical interview with John Doe tomorrow', time: '1 hour ago', unread: true },
    { id: 3, title: 'Job posting approved', message: 'React Developer job is now live', time: '2 hours ago', unread: false }
  ];

  const mockMessages = [
    { id: 1, from: 'John Doe', type: 'candidate', message: 'Thank you for the interview opportunity...', time: '10 min ago', avatar: 'JD' },
    { id: 2, from: 'Sarah Manager', type: 'team', message: 'Can you review the new candidate?', time: '1 hour ago', avatar: 'SM' },
    { id: 3, from: 'Mike Recruiter', type: 'team', message: 'Updated the job requirements as discussed', time: '3 hours ago', avatar: 'MR' }
  ];

  const mockUser = {
    name: 'John Doe',
    email: 'admin@company.com',
    initials: 'JD'
  };

  const companyData = {
    id: 'tech-co',
    name: 'Tech Co',
    logo: 'TC'
  };

  useEffect(() => {
    document.title = `${companyData?.name} Dashboard - Recrutees`;
  }, [companyData?.name]);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Layout animation variants
  const contentVariants = {
    expanded: {
      marginLeft: sidebarCollapsed ? 64 : 240,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 35,
        mass: 0.8,
      }
    }
  };

  // Page transition variants
  const pageVariants = {
    initial: { 
      opacity: 0, 
      y: 10
    },
    enter: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.15,
        ease: "easeOut"
      }
    },
    exit: { 
      opacity: 0,
      y: -10,
      transition: {
        duration: 0.1,
        ease: "easeIn"
      }
    }
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Header */}
      <UnifiedHeader 
        variant="dashboard"
        onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
        sidebarCollapsed={sidebarCollapsed}
        companyData={companyData}
        userRole="admin"
        user={mockUser}
        notifications={mockNotifications}
        messages={mockMessages}
      />
      {/* Company Sidebar */}
      <CompanyDashboardSidebar 
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
        userRole={userRole}
        companyData={companyData}
      />
      {/* Main Content */}
      <motion.main
        className="flex-1 overflow-auto"
        variants={contentVariants}
        animate="expanded"
        style={{ 
          marginLeft: sidebarCollapsed ? 0 : 0,
          paddingTop: 64 // Account for fixed header height
        }}
      >
        {/* Breadcrumb Navigation */}
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border" style={{ marginLeft: sidebarCollapsed ? 64 : 240 }}>
          <div className="px-4 md:px-6 py-3">
            <nav className="flex items-center space-x-2 text-sm" aria-label="Breadcrumb">
              <a href={`/company-dashboard-hub/${companyId}`} className="text-muted-foreground hover:text-foreground transition-colors">
                {companyData?.name}
              </a>
              {location?.pathname !== `/company-dashboard-hub/${companyId}` && location?.pathname !== `/company-dashboard-hub/${companyId}/overview` && (
                <>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-foreground font-medium capitalize">
                    {location?.pathname?.split('/')?.[3] || 'Overview'}
                  </span>
                </>
              )}
            </nav>
          </div>
        </div>
        
        <div className="p-4 md:p-6" style={{ marginLeft: sidebarCollapsed ? 64 : 240 }}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location?.pathname}
              variants={pageVariants}
              initial="initial"
              animate="enter"
              exit="exit"
            >
              <Suspense fallback={<ComponentLoader />}>
                <Routes location={location}>
                  {/* Default dashboard overview */}
                  <Route index element={<DashboardOverview companyData={companyData} userRole={userRole} />} />
                  <Route path="overview" element={<LazyLoadBoundary fallback={<DashboardCardSkeleton />}><DashboardOverview companyData={companyData} userRole={userRole} /></LazyLoadBoundary>} />
                  
                  {/* Jobs management */}
                  <Route path="jobs" element={<LazyLoadBoundary fallback={<TableRowSkeleton />}><JobsManager companyData={companyData} userRole={userRole} /></LazyLoadBoundary>} />
                  <Route path="jobs/active" element={<Navigate to={`/company-dashboard-hub/${companyId}/jobs`} replace />} />
                  <Route path="jobs/drafts" element={<Navigate to={`/company-dashboard-hub/${companyId}/jobs`} replace />} />
                  <Route path="jobs/analytics" element={<Navigate to={`/company-dashboard-hub/${companyId}/jobs`} replace />} />
                  
                  {/* Candidates */}
                  <Route path="candidates" element={<LazyLoadBoundary fallback={<TableRowSkeleton />}><CandidatesManager companyData={companyData} userRole={userRole} /></LazyLoadBoundary>} />
                  <Route path="candidates/pipeline" element={<Navigate to={`/company-dashboard-hub/${companyId}/candidates`} replace />} />
                  <Route path="candidates/shortlisted" element={<Navigate to={`/company-dashboard-hub/${companyId}/candidates`} replace />} />
                  <Route path="candidates/interviews" element={<Navigate to={`/company-dashboard-hub/${companyId}/candidates`} replace />} />
                  
                  {/* Team Management (Admin only) */}
                  {userRole === 'admin' && (
                    <Route path="team" element={<LazyLoadBoundary fallback={<TableRowSkeleton />}><TeamManagement companyData={companyData} userRole={userRole} /></LazyLoadBoundary>} />
                  )}
                  
                  {/* Company Profile */}
                  <Route path="profile" element={<LazyLoadBoundary><CompanyProfile companyData={companyData} userRole={userRole} /></LazyLoadBoundary>} />
                  {/* Locations Management */}
                  <Route path="locations" element={<LazyLoadBoundary><LocationsManagement companyId={companyId} /></LazyLoadBoundary>} />
                  {/* Analytics & Reports */}
                  <Route path="analytics" element={<LazyLoadBoundary fallback={<AnalyticsChartSkeleton />}><AnalyticsReports companyData={companyData} userRole={userRole} /></LazyLoadBoundary>} />
                  
                  {/* Billing & Subscription (Admin only) */}
                  {userRole === 'admin' && (
                    <Route path="billing" element={<LazyLoadBoundary><BillingSubscription companyData={companyData} userRole={userRole} /></LazyLoadBoundary>} />
                  )}
                  
                  {/* Redirect unauthorized access */}
                  {userRole === 'recruiter' && (
                    <>
                      <Route path="team" element={<Navigate to={`/company-dashboard-hub/${companyId}`} replace />} />
                      <Route path="billing" element={<Navigate to={`/company-dashboard-hub/${companyId}`} replace />} />
                    </>
                  )}
                </Routes>
              </Suspense>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.main>
    </div>
  );
};

export default CompanyDashboardHub;